'use client';

/**
 * Shared style atoms for the editorial (`se3`) test versions of the offering
 * body sections (Reasons to Invest, About, Team, Terms, Perks, Why StartEngine,
 * How Investing Works …).
 *
 * Every editorial section reuses the same visual language from the Claude
 * offering handoff (`AtomBeam Offering.dc.html` / `SambaNova Offering.dc.html`):
 *   - a tracked, uppercase GOLD eyebrow over each section
 *   - IBM Plex Serif sub-headings, IBM Plex Sans body copy
 *   - white cards on cream with a #E8E1D6 hairline edge and gold accents
 *
 * Most values come from `@startengine/design-system.se3.tokens`. A few accent
 * shades in the handoff (the darker gold text #856528 used on text accents,
 * the #4C463E body grey and #6D665A muted grey) are not in the published token
 * set, so they are pinned here as local constants.
 */
import { se3Colors, se3FontFamily } from '@startengine/design-system.se3.tokens';

// Contrast-safe accents not present in se3Colors (see file header).
export const ACCENT_GOLD = '#856528';
export const GOLD_TEXT = ACCENT_GOLD;
export const BODY_TEXT = '#4C463E';
export const MUTED_TEXT = '#6D665A';
export const TEAL = se3Colors.seDark; // #185C65 — editorial sub-accent / links

export const editorialColors = {
  accentGold: ACCENT_GOLD,
  goldText: GOLD_TEXT,
  body: BODY_TEXT,
  muted: MUTED_TEXT,
  teal: TEAL,
  ink: se3Colors.ink, // #221E19
  edge: se3Colors.edge, // #E8E1D6
  card: se3Colors.card, // #FFFFFF
  cream2: se3Colors.cream2, // #F5EFE4
};

export const fonts = se3FontFamily;

export const eyebrowSx = {
  fontFamily: se3FontFamily.sans,
  fontWeight: 600,
  fontSize: '11px',
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: ACCENT_GOLD,
  marginBottom: '20px',
};

export const bodySx = {
  fontFamily: se3FontFamily.sans,
  // PD-36055 (Hannah): body copy in the design is smaller and lighter than the
  // first pass — 14px with a 500-weight emphasis (was 15px / 600).
  fontSize: '14px',
  lineHeight: 1.7,
  color: BODY_TEXT,
  '& a': { color: `${TEAL} !important`, textDecoration: 'underline', textUnderlineOffset: '2px' },
  '& strong, & b': { color: se3Colors.ink, fontWeight: 500 },
};

export const serifHeadingSx = {
  fontFamily: se3FontFamily.serif,
  fontWeight: 500,
  fontSize: '21px',
  lineHeight: 1.2,
  color: se3Colors.ink,
};

export const disclaimerSx = {
  fontFamily: se3FontFamily.sans,
  fontStyle: 'italic',
  fontSize: '11.5px',
  lineHeight: 1.6,
  color: MUTED_TEXT,
};

// White card with the #E8E1D6 hairline used across editorial sections.
export const cardSx = {
  backgroundColor: se3Colors.card,
  border: `1px solid ${se3Colors.edge}`,
  borderRadius: '14px',
};

// Outer wrapper that keeps the section transparent so the page cream shows
// through (the reskin root unifies the background).
export const sectionWrapSx = {
  fontFamily: se3FontFamily.sans,
  backgroundColor: 'transparent',
  maxWidth: '100%',
};
